import React from 'react';
import type { PatientRecord } from '../../patients/types';
import { formatPatientMoney, ppEyebrowClass } from '../../patients/utils';
import { Icon } from '../Icon/Icon';
import { Pill } from '../Pill/Pill';

export interface PatientVisitHistoryProps {
  patient: PatientRecord;
  /**
   * v1 showed "5 of 18" — a truncation notice for a table that isn't
   * truncated. A patient has a couple of dozen visits in a lifetime, so the
   * table shows all of them and the counter says nothing useful.
   */
  showCounter?: boolean;
}

const GRID =
  'grid grid-cols-[1.1fr_2fr_1.3fr_0.8fr_0.9fr] items-center px-[18px]';

function visitStatusTone(status: string): 'success' | 'gold' | 'ai' | 'neutral' {
  const normalized = status.toLowerCase();
  if (normalized.includes('complete')) return 'success';
  if (normalized.includes('cancel') || normalized.includes('miss') || normalized.includes('no-show')) {
    return 'gold';
  }
  if (normalized.includes('sched') || normalized.includes('plan')) return 'ai';
  return 'neutral';
}

export function PatientVisitHistory({ patient: p, showCounter = true }: PatientVisitHistoryProps) {
  return (
    <div className="pp-fade mt-4" style={{ animationDelay: '150ms' }}>
      <div className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
        <div className="flex items-center gap-2 border-t-[3px] border-t-[var(--ai-4)] px-[18px] pb-[13px] pt-[15px]">
          <Icon kind="list" size={14} stroke={2} style={{ color: 'var(--ai-4)' }} />
          <span className={ppEyebrowClass} style={{ color: 'var(--ai-4)' }}>
            Visit history
          </span>
          {showCounter ? (
            <span className="ml-auto font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
              {p.history.length} of {p.visits}
            </span>
          ) : null}
        </div>
        {p.history.length === 0 ? (
          <div className="border-t border-[var(--cream-2)] px-[18px] py-8 text-center text-[13px] text-[var(--ink-3)]">
            No visit history on file yet.
          </div>
        ) : (
          <>
            <div
              className={`${GRID} border-b border-t border-[var(--cream-2)] bg-[var(--cream-0)] py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--ink-3)]`}
            >
              <div>Date</div>
              <div>Procedure</div>
              <div>Provider</div>
              <div className="text-right">Production</div>
              <div className="text-right">Status</div>
            </div>
            {p.history.map((r, i) => (
              <div
                key={`${r.date}-${r.proc}-${i}`}
                className={`${GRID} border-b border-[var(--cream-2)] py-[13px] text-[13px] last:border-b-0`}
              >
                <div className="font-[var(--font-mono)] text-xs text-[var(--ink-2)]">{r.date}</div>
                <div className="font-medium text-[var(--ink-0)]">{r.proc || '—'}</div>
                <div className="text-[var(--ink-2)]">{r.prov || '—'}</div>
                <div className="text-right font-[var(--font-mono)] font-semibold text-[var(--ink-1)]">
                  {formatPatientMoney(r.prod)}
                </div>
                <div className="text-right">
                  <Pill tone={visitStatusTone(r.status)} dot>
                    {r.status}
                  </Pill>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
